import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Box, Button, Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import FileOpenOutlinedIcon from '@mui/icons-material/FileOpenOutlined';
import { useMutation, useQuery } from '@tanstack/react-query';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useApi } from '@bumble/app/hooks/use-api';
import { UserAgent } from '@bumble/app/__generated__/data-contracts';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { apiError } from '@bumble/app/utils/api-error';
import { DocumentUploader } from '@bumble/app/components/documents/DocumentUploader';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { DocumentEdit } from '@bumble/app/components/documents/DocumentEdit';
import { DocumentViewer, getDocumentKind } from '@bumble/app/components/documents/DocumentViewer';

export const Documents = () => {
  const api = useApi();

  const [ownerList, setOwnerList] = useState<any[]>([]);
  const [owner, setOwner] = useState<any>(ownerList[0]);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [showUploader, setShowUploader] = useState<boolean>(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);

  const handleDownload = (url: string) => {
    window.open(url, '_blank', 'noopener');
  };

  const handleView = () => {
    setShowViewModal(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'filename',
      headerName: 'File name',
      width: 600,
      disableColumnMenu: true,
    }, {
      field: 'view',
      headerName: 'View',
      width: 120,
      renderCell: (params) => {
        const isUnsupported = getDocumentKind(params.row.filename) === 'unsupported';
        return (
          <Button
            variant="outlined"
            size="small"
            onClick={handleView}
            disabled={isUnsupported}
            endIcon={<FileOpenOutlinedIcon fontSize="small" />}
          >
            View
          </Button>
        );
      },
      disableColumnMenu: true,
    }, {
      field: 's3_download_url',
      headerName: 'Download',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleDownload(params.value)}
          endIcon={<DownloadIcon fontSize="small" />}
        >
          Download
        </Button>
      ),
      disableColumnMenu: true,
    }, {
      field: 'size',
      headerName: 'File size (Bytes)',
      width: 150,
      disableColumnMenu: true,
    }, {
      field: 'uploaded_at_utc',
      headerName: 'Upload time',
      width: 250,
      renderCell: (params) => (new Date(params.value).toLocaleString()),
      disableColumnMenu: true,
    }, {
      field: 'entity_name',
      headerName: 'Entity',
      width: 200,
      disableColumnMenu: true,
    }, {
      field: 'node_name',
      headerName: 'CPE name',
      width: 200,
      disableColumnMenu: true,
    },
  ];

  const { data: data3 } = useQuery(
    ['document-nodes'],
    api.documentGetDeviceNameIdPairApiV1DocumentGetDeviceNameIdPairGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    if (data3?.data && data3?.data.length) {
      const tmp = data3?.data.map((e: any) => ({
        label: e.name,
        value: e.id,
      }));
      setOwnerList(tmp);
      setOwner(tmp[0]);
    }
  }, [data3]);

  const { isLoading, data, refetch, isRefetching } = useQuery(
    ['documents', owner?.value],
    api.getDocumentsApiV1GetDocumentsGet.bind(null, { node_id: owner?.value }),
    { enabled: !!owner?.value, cacheTime: 0 },
  );

  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedRow(data?.data.filter(item => item.document_id === selectedRowItem)[0]);
    }
  }, [rowSelectionModel, selectedRowItem, data]);

  const removeDocument = useMutation(api.removeDocumentApiV1RemoveDocumentPost, {
    onSuccess() {
      toast.success('A document removed successfully.');
      setLoadingModalOpen(false);
      refetch();
    },
    onError(error: any) {
      apiError(error);
      setLoadingModalOpen(false);
    },
  });

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
  };

  const changeOwner = (val: any) => { setOwner(val) };

  const handleUpload = () => { setShowUploader(true) };

  const handleChange = () => { setShowEditModal(true) };

  const handleDelete = () => { setShowConfirmDelete(true) };

  const handleDeleteConfirm = () => {
    setLoadingModalOpen(true);
    removeDocument.mutate({ document_id: selectedRowItem });
  };

  const handleCloseDocumentUploader = () => {
    setShowUploader(false);
    refetch();
  };

  const handleRefresh = () => { refetch() };

  return (
    <PageContent>
      <PageHeader pageName="My Documents" count={data?.data?.length || 0} />
      <Box sx={{ mb: 5, display: 'flex', gap: 2 }}>
        <Select options={ownerList} defaultValue={ownerList[0]} value={owner} onChange={changeOwner} styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            borderRadius: '8px',
            width: '300px',
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            width: '300px',
          }),
        }}/>
      </Box>
      {isLoading ? (
        <Loading />
      ) : (
        <Card elevation={0}>
          <CardContent>
            <DataTableGrid
              rows={
                data?.data.map((item) => ({ ...item, id: item.document_id })) || []
              }
              columns={columns}
              handleSelectionChange={handleSelectionChange}
              rowSelectionModel={rowSelectionModel}
              itemsSelected={rowSelectionModel.length === 1}
              newLabel="Upload"
              newOnClick={handleUpload}
              getRowId={(row: UserAgent) => row?.user_agent_id}
              refreshData={handleRefresh}
              isRefetching={isRefetching}
              menuItems={[
                {
                  label: 'Change name',
                  icon: <EditIcon color="info" fontSize="small" />,
                  onClick: handleChange,
                },
                {
                  label: 'Delete',
                  icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
                  onClick: handleDelete,
                  hasDivider: true,
                },
              ]}
            />
          </CardContent>
        </Card>
      )}
      {showUploader &&
        <DocumentUploader
          open={showUploader}
          handleClose={handleCloseDocumentUploader}
          owner={owner}
        />
      }
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleDeleteConfirm}
          title="Delete document?"
          description={`Are you certain you wish to proceed with deleting ${selectedRowItem}?
          Please note that this action is irreversible and will result in the permanent removal of document.`}
        />
      )}
      {showEditModal && selectedRowItem && (
        <DialogStandard
          open={showEditModal}
          handleClose={() => setShowEditModal(false)}
          title="Change name"
        >
          <DocumentEdit
            onSubmit={() => {
              setShowEditModal(false);
              refetch();
            }}
            documentId={selectedRowItem}
            filename={selectedRow?.filename}
          />
        </DialogStandard>
      )}
      {showViewModal && selectedRowItem && (
        <DialogStandard
          open={showViewModal}
          handleClose={() => setShowViewModal(false)}
          title="View Document"
          size="xl"
        >
          <DocumentViewer filename={selectedRow?.filename} url={selectedRow?.s3_download_url} />
        </DialogStandard>
      )}
      {loadingModalOpen && <PageLoading content="Working on it..." />}
    </PageContent>
  );
};
